import { addIgnoreLabel } from "../../Sites/SiteAboutPage/SiteAboutPageUtils/addIgnoreLabel";
import { IgnoreZone } from "../../Sites/SiteAboutPage/SiteAboutPageUtils/siteAboutInterfaces";
import { IgnoreZoneAssignment } from "../types";
import { buildAvailableMetrics, getAllMetricIds } from "./assignmentMetricLabels";
import { getNextAssignmentColor } from "./getNextAssignmentColor";

const hasCoordinates = (coordinates: number[][] | number[][][] | undefined): boolean => {
  if (!Array.isArray(coordinates) || coordinates.length === 0) {
    return false;
  }
  const polygons = coordinates as number[][][];
  return polygons.some(
    (polygon) => Array.isArray(polygon) && polygon.length >= 3
  );
};

const normalizeCoordinates = (
  coordinates: number[][] | number[][][]
): string => JSON.stringify(coordinates);

const createLocalId = (): string =>
  `assignment-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

interface RowFingerprint {
  color: string;
  coordinatesKey: string;
  metricIdsKey: string;
  coordinates: number[][][];
  metricIds: number[];
  isIgnoreAll: boolean;
  sourceApiLabels: string[];
}

export const apiIgnoreZonesToAssignments = (
  apiRows: IgnoreZone[]
): IgnoreZoneAssignment[] => {
  const labeledRows = addIgnoreLabel(apiRows) as IgnoreZone[];
  const availableMetrics = buildAvailableMetrics(labeledRows);
  const allMetricIds = getAllMetricIds(availableMetrics);

  const fingerprints = new Map<string, RowFingerprint>();
  const usedColors = new Set<string>();

  labeledRows.forEach((row) => {
    if (!hasCoordinates(row.coordinates)) {
      return;
    }

    const isIgnoreAll = Number(row.zone_type) === 0;
    const metricIds = isIgnoreAll
      ? [...allMetricIds]
      : [Number(row.metric_id)];

    const coordinatesKey = normalizeCoordinates(row.coordinates);
    const metricIdsKey = [...metricIds].sort((a, b) => a - b).join(",");
    const fingerprintKey = `${row.line_color}|${coordinatesKey}|${metricIdsKey}`;

    const existing = fingerprints.get(fingerprintKey);
    if (existing) {
      if (!existing.sourceApiLabels.includes(row.label)) {
        existing.sourceApiLabels.push(row.label);
      }
      return;
    }

    let color = row.line_color;
    if (usedColors.has(color)) {
      color = getNextAssignmentColor([...usedColors]);
    }
    usedColors.add(color);

    fingerprints.set(fingerprintKey, {
      color,
      coordinatesKey,
      metricIdsKey,
      coordinates: JSON.parse(coordinatesKey) as number[][][],
      metricIds,
      isIgnoreAll,
      sourceApiLabels: [row.label],
    });
  });

  return [...fingerprints.values()].map((entry, index) => ({
    localId: createLocalId(),
    name: `Zone ${index + 1}`,
    color: entry.color,
    coordinates: entry.coordinates,
    metricIds: entry.metricIds,
    isIgnoreAll: entry.isIgnoreAll,
    sourceApiLabels: entry.sourceApiLabels,
  }));
};
